// The printed traveler for one work card: its steps, its materials, its checklist lines with
// a box to tick, and a code for each SOP. Everything on it is assembled from the card's
// operations and items; nothing is typed onto a traveler.

import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { useRows } from '../lib/api';
import { useCardReferences } from '../lib/cardrefs';
import { qty } from '../lib/format';
import { jobTitle, useAllJobs } from '../lib/jobs';
import { Btn, Empty, Loading } from '../ui/kit';
import { QR } from './inventory/KanbanCard';
import { sopCode } from './Refs';

export function Traveler() {
  const { cardId } = useParams();
  const [params] = useSearchParams();
  const card = useRows('work_card', { eq: { id: cardId } }).rows[0];
  const steps = useRows('work_card_step', { eq: { work_card_id: cardId }, order: ['sort'] });
  const label = useRows('label', { eq: { kind: 'card', target_id: cardId } }).rows[0];
  const centers = useRows('work_center');
  const items = useRows('job_item', { in: { id: ((card?.items as string[]) ?? []).filter((id) => !id.includes('~')) }, enabled: !!card });
  const all = useAllJobs();
  const refs = useCardReferences(card, steps.rows);
  const printed = useRef(false);
  useCrumb([{ label: 'Shop', to: '/shop' }, { label: card ? `Traveler · card #${card.number}` : 'Traveler' }]);

  const ready = !!card && !steps.loading && !refs.loading && !all.loading;
  useEffect(() => {
    if (!ready || printed.current || params.get('print') !== '1') return;
    printed.current = true;
    const t = window.setTimeout(() => window.print(), 600);
    return () => window.clearTimeout(t);
  }, [ready, params]);

  if (!card && steps.loading) return <Loading />;
  if (!card) return <Empty>This card is no longer there.</Empty>;
  const j = all.jobs.find((x) => x.job.id === card.job_id);
  const bom = ((card.bom as { description: string; qty: number; unit?: string | null }[]) ?? []).filter((x) => Number(x.qty) > 0);

  return (
    <div>
      <div className="row mid noprint" style={{ marginBottom: 10, gap: 8 }}>
        <Btn primary read onClick={() => window.print()}>
          Print
        </Btn>
        <small>Checklist lines and SOP codes are pulled from the card's operations and items.</small>
      </div>
      <div className="traveler">
        <div className="tv-head">
          <div>
            <h1>
              Card #{card.number} · {card.name}
            </h1>
            <div>{j ? jobTitle(j) : 'Job'}</div>
            <div>
              Items: {items.rows.map((i) => i.number).join(', ') || '—'} · {Number(card.hours_standard).toFixed(1)} h standard
            </div>
          </div>
          {label && (
            <div className="tv-code">
              <QR text={label.code} size={84} />
              <small>{label.code}</small>
            </div>
          )}
        </div>

        {items.rows.some((i) => i.note) && (
          <>
            <h2>Notes on the items</h2>
            {items.rows
              .filter((i) => i.note)
              .map((i) => (
                <div key={i.id} className="tv-note">
                  <b>{i.number}</b> {i.name ? `· ${i.name} ` : ''}— <span style={{ whiteSpace: 'pre-wrap' }}>{i.note}</span>
                </div>
              ))}
          </>
        )}

        <h2>Steps</h2>
        <table>
          <tbody>
            {steps.rows.map((x) => (
              <tr key={x.id}>
                <td>{x.name}</td>
                <td>
                  {qty(x.qty)} {x.unit ?? ''}
                </td>
                <td>{centers.rows.find((w) => w.id === x.work_center_id)?.name ?? ''}</td>
                <td>{Math.round(Number(x.minutes_standard))} min</td>
              </tr>
            ))}
          </tbody>
        </table>

        {bom.length > 0 && (
          <>
            <h2>Materials this card takes from stock</h2>
            <table>
              <tbody>
                {bom.map((x, i) => (
                  <tr key={i}>
                    <td>{x.description}</td>
                    <td>
                      {qty(x.qty)} {x.unit ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <h2>Checklist</h2>
        {refs.assembled.checklist.length === 0 && <div>No checklist lines apply to this card.</div>}
        {refs.assembled.checklist.map((c) => (
          <div key={c.line.id} className="tv-check">
            <span className="tv-box" />
            <span>
              {c.line.text}
              {c.item_numbers.length > 0 && <small> · {c.item_numbers.join(', ')}</small>}
              {c.line.required && <b> · must be ticked to close</b>}
            </span>
          </div>
        ))}

        {refs.assembled.sops.length > 0 && (
          <>
            <h2>SOPs · scan to open</h2>
            <div className="tv-sops">
              {refs.assembled.sops.map((s) => (
                <div key={s.id} className="tv-sop">
                  <QR text={sopCode(s.id)} size={72} />
                  <div>
                    <b>{s.title}</b>
                    <small>{steps.rows.find((x) => x.operation_id === s.operation_id)?.name ?? ''}</small>
                    <small>{sopCode(s.id)}</small>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
